import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { addressLine } from '../../lib/format.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, fieldErrors, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import {
  AddressField,
  addressDraftFrom,
  addressIsUsable,
  emptyAddress,
  type AddressDraft,
} from '../AddressField.js'
import { ClientPicker } from '../ClientPicker.js'
import { DialogBody, DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { Button, Field, Input, Select, Textarea, ToggleRow, humanise } from '../ui.js'

/**
 * The client record's editors.
 *
 * Creating a client is not a second form: it is `ClientPicker`, search first,
 * because the person on the phone is often already a client and a second
 * "Tom Smith" splits his history in two. Decision 60. Everything past the name
 * and the first contact is filled in on the client's own page, which is where
 * these dialogs open from.
 */

export type ClientRecord = RouterOutputs['clients']['byId']
type SiteRecord = ClientRecord['sites'][number]

const CLIENT_TYPES = ['RESIDENTIAL', 'COMMERCIAL', 'STRATA', 'GOVERNMENT', 'REAL_ESTATE'] as const

function useInvalidateClients() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries(trpc.clients.pathFilter())
    // A renamed client or a moved property is on every job card that names it.
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }
}

/** Composes `raw` for a typed address so it reads like an imported one. */
function addressPayload(address: AddressDraft) {
  return { ...address, raw: address.raw ?? addressLine(address) }
}

/* -------------------------------------------------------------------------- */

export function NewClientDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()

  return (
    <Modal open={open} onClose={onClose} labelledBy="new-client-title" className="max-w-xl">
      {open ? (
        <>
          <DialogHeader
            id="new-client-title"
            title="New client"
            description="Search first — the caller may already be a client. Pick them to open their record, or create them."
            onClose={onClose}
          />
          <DialogBody className="pb-6">
            <ClientPicker
              id="new-client-search"
              autoFocus
              value={null}
              onChange={(client) => {
                if (!client) return
                onClose()
                navigate(`/clients/${client.id}`)
              }}
            />
          </DialogBody>
        </>
      ) : null}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */

export function ClientEditDialog({
  client,
  open,
  onClose,
}: {
  client: ClientRecord
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="edit-client-title" className="max-w-2xl">
      {open ? <ClientEditForm client={client} onClose={onClose} /> : null}
    </Modal>
  )
}

function ClientEditForm({ client, onClose }: { client: ClientRecord; onClose: () => void }) {
  const trpc = useTRPC()
  const { can } = useSession()
  const invalidate = useInvalidateClients()

  const tiers = useQuery({ ...trpc.catalog.priceTiers.queryOptions(), enabled: can('CATALOG_VIEW') })

  const [name, setName] = useState(client.name)
  const [type, setType] = useState(client.type)
  const [email, setEmail] = useState(client.email ?? '')
  const [phone, setPhone] = useState(client.phone ?? '')
  const [website, setWebsite] = useState(client.website ?? '')
  const [taxNumber, setTaxNumber] = useState(client.taxNumber ?? '')
  const [terms, setTerms] = useState(String(client.paymentTermsDays))
  const [priceTierId, setPriceTierId] = useState(client.priceTierId ?? '')
  const [onHold, setOnHold] = useState(client.isOnCreditHold)
  const [taxExempt, setTaxExempt] = useState(client.taxExempt)
  const [notes, setNotes] = useState(client.notes ?? '')
  const [billing, setBilling] = useState<AddressDraft>(() => addressDraftFrom(client.billingAddress))
  const [billingTouched, setBillingTouched] = useState(false)

  const save = useMutation(
    trpc.clients.update.mutationOptions({
      onSuccess: () => {
        invalidate()
        onClose()
      },
    }),
  )

  const errors = fieldErrors(save.error)
  const billingReady = addressIsUsable(billing)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate({
      id: client.id,
      name: name.trim(),
      type,
      email: email.trim() || null,
      phone: phone.trim() || null,
      website: website.trim() || null,
      taxNumber: taxNumber.trim() || null,
      paymentTermsDays: Number(terms) || 0,
      priceTierId: priceTierId || null,
      isOnCreditHold: onHold,
      taxExempt,
      notes: notes.trim() || null,
      // Only an address somebody actually edited is sent. Re-sending the one
      // that loaded would stamp a geocode nobody checked.
      ...(billingTouched && billingReady ? { billingAddress: addressPayload(billing) } : {}),
    })
  }

  return (
    <form className="flex min-h-0 flex-col" onSubmit={submit} noValidate>
      <DialogHeader
        id="edit-client-title"
        title="Edit client"
        description={`Client ${client.number}`}
        onClose={onClose}
      />
      <DialogBody>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Field label="Name" htmlFor="client-name" required error={errors['name']}>
            <Input id="client-name" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Kind of client" htmlFor="client-type">
            <Select
              id="client-type"
              value={type}
              onChange={(event) => setType(event.target.value as ClientRecord['type'])}
            >
              {CLIENT_TYPES.map((option) => (
                <option key={option} value={option}>
                  {humanise(option)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Accounts email"
            htmlFor="client-email"
            error={errors['email']}
            hint="Only used when nobody on the job or the client is named for invoices."
          >
            <Input
              id="client-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="Main phone" htmlFor="client-phone" error={errors['phone']}>
            <Input
              id="client-phone"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </Field>
          <Field label="Website" htmlFor="client-website" error={errors['website']}>
            <Input
              id="client-website"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </Field>
          <Field label="ABN" htmlFor="client-abn" error={errors['taxNumber']}>
            <Input
              id="client-abn"
              inputMode="numeric"
              value={taxNumber}
              onChange={(event) => setTaxNumber(event.target.value)}
            />
          </Field>
        </div>

        <fieldset className="flex flex-col gap-3 rounded-xl bg-ink-50 p-3 ring-1 ring-inset ring-ink-200">
          <legend className="sr-only">Billing</legend>
          <p className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">Billing</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Payment terms (days)"
              htmlFor="client-terms"
              error={errors['paymentTermsDays']}
            >
              <Input
                id="client-terms"
                type="number"
                min={0}
                max={365}
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
              />
            </Field>
            {tiers.data && tiers.data.length > 0 ? (
              <Field label="Price tier" htmlFor="client-tier">
                <Select
                  id="client-tier"
                  value={priceTierId}
                  onChange={(event) => setPriceTierId(event.target.value)}
                >
                  <option value="">Standard prices</option>
                  {tiers.data.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
          </div>
          <ToggleRow
            label="On credit hold"
            description="New work should not be booked until the account is settled."
            checked={onHold}
            onChange={setOnHold}
          />
          <ToggleRow
            label="Tax exempt"
            description="No GST on this client's invoices."
            checked={taxExempt}
            onChange={setTaxExempt}
          />
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-ink-700">Billing address</p>
          <AddressField
            id="client-billing"
            value={billing}
            onChange={(next) => {
              setBilling(next)
              setBillingTouched(true)
            }}
          />
          {billingTouched && !billingReady ? (
            <p className="text-xs text-amber-700">
              A street and a suburb are needed before the billing address is saved.
            </p>
          ) : null}
        </div>

        <Field label="Notes" htmlFor="client-notes" hint="For the office. Technicians do not see these.">
          <Textarea id="client-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </DialogBody>
      <DialogFooter error={save.error ? errorMessage(save.error) : null}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={save.isPending} disabled={!name.trim()}>
          Save client
        </Button>
      </DialogFooter>
    </form>
  )
}

/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */

const ACCESS_METHODS = [
  'CLIENT_PRESENT',
  'KEY_SAFE',
  'GATE_CODE',
  'AGENT_KEY',
  'OPEN_ACCESS',
  'OTHER',
] as const

export function SiteDialog({
  clientId,
  site,
  open,
  onClose,
}: {
  clientId: string
  /** Absent to add a new property. */
  site: SiteRecord | null
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="site-title" className="max-w-2xl">
      {open ? <SiteForm clientId={clientId} site={site} onClose={onClose} /> : null}
    </Modal>
  )
}

function SiteForm({
  clientId,
  site,
  onClose,
}: {
  clientId: string
  site: SiteRecord | null
  onClose: () => void
}) {
  const trpc = useTRPC()
  const invalidate = useInvalidateClients()
  const done = { onSuccess: () => (invalidate(), onClose()) }

  const [address, setAddress] = useState<AddressDraft>(() =>
    site ? addressDraftFrom(site.address) : emptyAddress(),
  )
  const [addressTouched, setAddressTouched] = useState(false)
  const [name, setName] = useState(site?.name ?? '')
  const [accessMethod, setAccessMethod] = useState(site?.accessMethod ?? '')
  const [gateCode, setGateCode] = useState(site?.gateCode ?? '')
  const [accessNotes, setAccessNotes] = useState(site?.accessNotes ?? '')
  const [hazardNotes, setHazardNotes] = useState(site?.hazardNotes ?? '')
  const [parkingNotes, setParkingNotes] = useState(site?.parkingNotes ?? '')

  const create = useMutation(trpc.clients.createSite.mutationOptions(done))
  const update = useMutation(trpc.clients.updateSite.mutationOptions(done))
  const archive = useMutation(trpc.clients.setSiteArchived.mutationOptions(done))

  const ready = addressIsUsable(address)
  const error = create.error ?? update.error ?? archive.error

  const knowledge = {
    name: name.trim() || null,
    accessMethod: (accessMethod || null) as SiteRecord['accessMethod'],
    gateCode: gateCode.trim() || null,
    accessNotes: accessNotes.trim() || null,
    hazardNotes: hazardNotes.trim() || null,
    parkingNotes: parkingNotes.trim() || null,
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!ready) return
    if (site) {
      update.mutate({
        id: site.id,
        ...knowledge,
        // An untouched address is left alone, so its place id and its point
        // on the map survive an edit to the gate code.
        ...(addressTouched ? { address: addressPayload(address) } : {}),
      })
    } else {
      create.mutate({ clientId, ...knowledge, address: addressPayload(address) })
    }
  }

  return (
    <form className="flex min-h-0 flex-col" onSubmit={submit} noValidate>
      <DialogHeader
        id="site-title"
        title={site ? 'Edit site' : 'Add a site'}
        description={
          site
            ? `Site ${site.number}. What a technician needs to know before they arrive.`
            : 'A place this client gets work done. It keeps its own history and access notes.'
        }
        onClose={onClose}
      />
      <DialogBody>
        <AddressField
          id="site-address"
          autoFocus={!site}
          value={address}
          onChange={(next) => {
            setAddress(next)
            setAddressTouched(true)
          }}
        />
        <Field
          label="Name it (optional)"
          htmlFor="site-name"
          hint="Only worth it when this client has several — “Warehouse 3”, “Tower B”."
        >
          <Input id="site-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <fieldset className="flex flex-col gap-3 rounded-xl bg-ink-50 p-3 ring-1 ring-inset ring-ink-200">
          <legend className="sr-only">Getting in</legend>
          <p className="text-[11px] font-semibold tracking-wider text-ink-500 uppercase">Getting in</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="How to get in" htmlFor="site-access-method">
              <Select
                id="site-access-method"
                value={accessMethod}
                onChange={(event) => setAccessMethod(event.target.value as typeof accessMethod)}
              >
                <option value="">Nobody has said</option>
                {ACCESS_METHODS.map((option) => (
                  <option key={option} value={option}>
                    {humanise(option)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Gate or key safe code" htmlFor="site-gate">
              <Input id="site-gate" value={gateCode} onChange={(event) => setGateCode(event.target.value)} />
            </Field>
          </div>
          <Field label="Access notes" htmlFor="site-access">
            <Textarea
              id="site-access"
              value={accessNotes}
              onChange={(event) => setAccessNotes(event.target.value)}
              placeholder="Side gate, ring the tenant first…"
            />
          </Field>
          <Field label="Parking" htmlFor="site-parking">
            <Input
              id="site-parking"
              value={parkingNotes}
              onChange={(event) => setParkingNotes(event.target.value)}
            />
          </Field>
        </fieldset>

        <Field label="Hazards" htmlFor="site-hazards" hint="Shown in amber on every job here.">
          <Textarea
            id="site-hazards"
            value={hazardNotes}
            onChange={(event) => setHazardNotes(event.target.value)}
            placeholder="Dog in the back yard, asbestos eaves…"
          />
        </Field>
      </DialogBody>
      <DialogFooter
        error={error ? errorMessage(error) : null}
        hint={ready ? null : 'A street and a suburb are the minimum.'}
      >
        {site ? (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto"
            loading={archive.isPending}
            onClick={() => archive.mutate({ id: site.id, archived: !site.archivedAt })}
          >
            {site.archivedAt ? 'Restore site' : 'Archive site'}
          </Button>
        ) : null}
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={create.isPending || update.isPending}
          disabled={!ready}
        >
          {site ? 'Save site' : 'Add site'}
        </Button>
      </DialogFooter>
    </form>
  )
}
